import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle2, FileText, MapPin, Loader2, Info } from 'lucide-react';
import { sendFormEmail } from '../utils/sendFormEmail';
const CENTER: [number, number] = [47.3087, -122.0032]; // Black Diamond, WA
// Haversine formula to calculate distance in miles
function getDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
function getZoneInfo(miles: number) {
  if (miles <= 7) return {
    zone: 1,
    fee: 40
  };
  if (miles <= 13) return {
    zone: 2,
    fee: 75
  };
  if (miles <= 18) return {
    zone: 3,
    fee: 120
  };
  if (miles <= 22) return {
    zone: 4,
    fee: 150
  };
  return {
    zone: null,
    fee: 0
  }; // Custom pricing needed
}
interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function QuoteModal({
  isOpen,
  onClose
}: QuoteModalProps) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [addressInput, setAddressInput] = useState('');
  const [addressError, setAddressError] = useState('');
  const [isCalculatingDelivery, setIsCalculatingDelivery] = useState(false);
  const [deliveryZone, setDeliveryZone] = useState<number | null>(null);
  const [deliveryFee, setDeliveryFee] = useState(0);
  // Debounced address calculation
  useEffect(() => {
    if (!addressInput.trim()) {
      setDeliveryZone(null);
      setDeliveryFee(0);
      setAddressError('');
      return;
    }
    const timer = setTimeout(async () => {
      setIsCalculatingDelivery(true);
      setAddressError('');
      try {
        const query = encodeURIComponent(addressInput + ', WA');
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
        const data = await res.json();
        if (!data || data.length === 0) {
          setAddressError('Address not found. Please try a more specific address.');
          setDeliveryZone(null);
          setDeliveryFee(0);
        } else {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          const distance = getDistanceMiles(CENTER[0], CENTER[1], lat, lon);
          const zoneInfo = getZoneInfo(distance);
          setDeliveryZone(zoneInfo.zone);
          setDeliveryFee(zoneInfo.fee);
        }
      } catch (err) {
        setAddressError('Could not calculate delivery. Please try again.');
        setDeliveryZone(null);
        setDeliveryFee(0);
      } finally {
        setIsCalculatingDelivery(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [addressInput]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const inputs = form.querySelectorAll('input');
    const selects = form.querySelectorAll('select');
    const textarea = form.querySelector('textarea');
    const customerName = (inputs[0] as HTMLInputElement)?.value || '';
    const customerEmail = (inputs[2] as HTMLInputElement)?.value || '';
    await sendFormEmail({
      subject: '253Gravel — New Quote Request',
      'Full Name': customerName,
      Phone: (inputs[1] as HTMLInputElement)?.value || '',
      email: customerEmail,
      _replyto: customerEmail,
      _autoresponse: `Hi ${customerName}! Thank you for requesting a quote from 253 Gravel! We've received your details and will get back to you shortly with pricing and availability.\n\nIf you need anything sooner, call us at (206) 619-3742.\n\n- The 253 Gravel Team`,
      'Delivery Address': addressInput,
      'Delivery Zone': deliveryZone !== null ? `Zone ${deliveryZone} (+${deliveryFee})` : 'Outside standard zones',
      Material: (selects[0] as HTMLSelectElement)?.value || '',
      'Estimated Yardage': (selects[1] as HTMLSelectElement)?.value || '',
      'Project Details': textarea?.value || ''
    });
    setIsSubmitted(true);
  };
  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setIsSubmitted(false);
      setAddressInput('');
      setDeliveryZone(null);
      setDeliveryFee(0);
    }, 300);
  };
  const inputClass = 'w-full px-3.5 py-2.5 bg-white dark:bg-[#3b3b3b] border border-stone-200 dark:border-[#4c4c4c] rounded text-brand-charcoal dark:text-stone-200 placeholder-brand-muted dark:placeholder-stone-500 focus:ring-1 focus:ring-brand-orange/50 focus:border-brand-orange/50 transition-all text-sm';
  return <AnimatePresence data-id="element-681">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={handleClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]" data-id="element-682" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none" data-id="element-683">
            <div className="w-full max-w-2xl max-h-[90vh] bg-white dark:bg-[#474747] shadow-2xl flex flex-col rounded-2xl border border-brand-lightgray dark:border-[#5d5d5d] pointer-events-auto overflow-hidden" data-id="element-684">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 bg-white dark:bg-[#3b3b3b] border-b border-brand-lightgray dark:border-[#5d5d5d]" data-id="element-685">
                <div className="flex items-center space-x-3" data-id="element-686">
                  <FileText className="w-5 h-5 text-brand-orange" data-id="element-687" />
                  <h2 className="font-heading text-lg font-bold text-brand-charcoal dark:text-white" data-id="element-688">
                    Request a Quote
                  </h2>
                </div>
                <button onClick={handleClose} className="p-1.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white transition-colors rounded-full hover:bg-brand-lightgray dark:hover:bg-[#4c4c4c]" data-id="element-689">
                  <X className="w-5 h-5" data-id="element-690" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6" data-id="element-691">
                {isSubmitted ? <div className="flex flex-col items-center justify-center text-center space-y-5 py-8" data-id="element-692">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                damping: 15,
                stiffness: 200
              }} className="w-16 h-16 bg-[#05894d]/20 dark:bg-[#05894d]/20 rounded-full flex items-center justify-center" data-id="element-693">
                      <CheckCircle2 className="w-8 h-8 text-[#05894d] dark:text-[#05894d]" data-id="element-694" />
                    </motion.div>
                    <div data-id="element-695">
                      <h3 className="font-heading text-xl font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-696">
                        Quote Request Sent!
                      </h3>
                      <p className="text-stone-600 dark:text-stone-200 max-w-xs mx-auto" data-id="element-697">
                        Thanks for reaching out. We'll get back to you shortly
                        with pricing and availability.
                      </p>
                    </div>
                    <button onClick={handleClose} className="bg-brand-charcoal dark:bg-[#4c4c4c] hover:bg-brand-charcoal/90 dark:hover:bg-[#5d5d5d] text-white px-8 py-2.5 rounded-xl font-bold transition-colors" data-id="element-698">
                      Done
                    </button>
                  </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-699">
                    {/* Row 1: Name & Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-700">
                      <div data-id="element-701">
                        <label className="block text-xs font-semibold text-stone-800 dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-702">
                          Full Name *
                        </label>
                        <input type="text" required className={inputClass} placeholder="John Doe" autoComplete="name" data-id="element-703" />
                      </div>
                      <div data-id="element-704">
                        <label className="block text-xs font-semibold text-stone-800 dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-705">
                          Phone Number *
                        </label>
                        <input type="tel" required className={inputClass} placeholder="(253) 555-0123" autoComplete="tel" data-id="element-706" />
                      </div>
                    </div>

                    {/* Row 2: Email & Delivery Address */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-707">
                      <div data-id="element-708">
                        <label className="block text-xs font-semibold text-stone-800 dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-709">
                          Email Address *
                        </label>
                        <input type="email" required className={inputClass} placeholder="john@example.com" autoComplete="email" data-id="element-710" />
                      </div>
                      <div className="md:col-span-2" data-id="element-711">
                        <label className="flex items-center justify-between text-xs font-semibold text-stone-800 dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-712">
                          <span className="flex items-center" data-id="element-713">
                            <MapPin className="w-4 h-4 mr-2 text-stone-400" data-id="element-714" />
                            Delivery Address *
                          </span>
                          {isCalculatingDelivery && <span className="text-xs text-brand-orange flex items-center normal-case" data-id="element-715">
                              <Loader2 className="w-3 h-3 animate-spin mr-1" data-id="element-716" />
                              Calculating...
                            </span>}
                        </label>
                        <input type="text" required value={addressInput} onChange={e => setAddressInput(e.target.value)} className={inputClass} placeholder="123 Main St, Tacoma, WA" autoComplete="street-address" data-id="element-717" />
                        {addressError && <p className="text-xs text-red-500 mt-1" data-id="element-718">
                            {addressError}
                          </p>}
                        {!isCalculatingDelivery && !addressError && addressInput && deliveryZone !== null && <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center" data-id="element-719">
                              <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-720" />
                              Zone {deliveryZone} (
                              {deliveryFee === 0 ? 'Free Delivery' : `+$${deliveryFee} Delivery`}
                              )
                            </p>}
                        {!isCalculatingDelivery && !addressError && addressInput && deliveryZone === null && <p className="text-xs text-brand-orange mt-1 flex items-center" data-id="element-721">
                              <Info className="w-3 h-3 mr-1" data-id="element-722" />
                              Outside standard zones. We'll provide a custom
                              delivery quote.
                            </p>}
                      </div>
                    </div>

                    {/* Row 3: Material & Yardage */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-723">
                      <div data-id="element-724">
                        <label className="block text-xs font-semibold text-stone-800 dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-725">
                          Material Needed
                        </label>
                        <select className={inputClass} data-id="element-726">
                          <option data-id="element-727">Crushed Rock</option>
                          <option data-id="element-728">Pea Gravel</option>
                          <option data-id="element-729">Drain Rock</option>
                          <option data-id="element-730">Road Base</option>
                          <option data-id="element-731">Sand/Topsoil</option>
                          <option data-id="element-732">Bark & Mulch</option>
                          <option data-id="element-733">Sand</option>
                          <option data-id="element-734">River Rock</option>
                          <option data-id="element-735">Not Sure Yet</option>
                        </select>
                      </div>
                      <div data-id="element-736">
                        <label className="block text-xs font-semibold text-stone-800 dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-737">
                          Estimated Yardage
                        </label>
                        <select className={inputClass} data-id="element-738">
                          <option data-id="element-739">4 Loads</option>
                          <option data-id="element-740">5 Loads</option>
                          <option data-id="element-741">Other</option>
                        </select>
                      </div>
                    </div>

                    {/* Row 4: Project Details */}
                    <div data-id="element-742">
                      <label className="block text-xs font-semibold text-stone-800 dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-743">
                        Project Details (Optional)
                      </label>
                      <textarea rows={3} className={`${inputClass} resize-none`} placeholder="Tell us about your project or any delivery instructions..." data-id="element-744"></textarea>
                    </div>

                    <button type="submit" className="w-full bg-[#3b3b3b] hover:bg-[#2a2a2a] text-white py-3.5 rounded font-bold transition-colors flex items-center justify-center border border-[#4c4c4c]" data-id="element-745">
                      <Send className="w-4 h-4 mr-2" data-id="element-746" />
                      Send Quote Request
                    </button>
                  </form>}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}